import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import * as Sentry from "@sentry/react";
import { useAuth } from '@clerk/clerk-react';
import { Loader2 } from 'lucide-react';
import AuthService from './services/AuthService';
import AccountService from './services/AccountService';
import analytics from './services/AnalyticsService';
import { onAuthFailure, onRateLimited, setClerkTokenGetter } from './services/HttpService';
import { classifyProvisionFailure, provisionErrorCopy } from './utils/authErrors';
import { ErrorBoundary, Card, Button, ThemeStyles, toast } from './components/v2/V2Components';
import PendingDeletionBanner from './components/v2/PendingDeletionBanner';
import PaywallProvider from './components/v2/PaywallProvider';
import { ThemeProvider } from './ThemeContext';

// V2 Imports
import ClerkLogin from './pages/v2/ClerkLogin';
import ClerkRegister from './pages/v2/ClerkRegister';
import DashboardV2 from './pages/v2/Dashboard';
import InventoryV2 from './pages/Inventory';
import ItemDetails from './pages/ItemDetails';
import AddAsset from './pages/v2/AddAsset';
import AddRoom from './pages/v2/AddRoom';
import SpacesV2 from './pages/v2/Spaces';
import RoomDetailsV2 from './pages/v2/RoomDetails';
import HouseFloorPlanEditor from './pages/v2/HouseFloorPlanEditor';
import RoomEdit from './pages/v2/RoomEdit';
import PaintLibrary from './pages/v2/PaintLibrary';
import PaintForm from './pages/v2/PaintForm';
import FloorDetailForm from './pages/v2/FloorDetailForm';
import ProfileV2 from './pages/v2/Profile';
import MaintenanceV2 from './pages/v2/Maintenance';
import CreateTask from './pages/v2/CreateTask';
import DocumentsV2 from './pages/v2/Documents';
import Models3DV2 from './pages/v2/Models3D';
import LayoutV2 from './components/v2/Layout';
import FeatureGate from './components/v2/FeatureGate';
import { FEATURES } from './featureFlags';
import HouseListV2 from './pages/v2/HouseList';
import HouseFormV2 from './pages/v2/HouseForm';
import HouseDetailsV2 from './pages/v2/HouseDetails';
import UtilityDetailsPage from './pages/v2/UtilityDetails';
import NotificationsV2 from './pages/v2/Notifications';
import InsuranceForm from './pages/v2/InsuranceForm';
import MortgageForm from './pages/v2/MortgageForm';
import MortgageDetails from './pages/v2/MortgageDetails';
import MortgageStatementForm from './pages/v2/MortgageStatementForm';
import MortgageAmortization from './pages/v2/MortgageAmortization';
import SubscriptionV2 from './pages/v2/Subscription';
import SubscriptionSuccess from './pages/v2/SubscriptionSuccess';
import SubscriptionCancel from './pages/v2/SubscriptionCancel';

const CLERK_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY || import.meta.env.CLERK_PUBLISHABLE_KEY;

// ClerkSync bridges Clerk's auth state into the local React state.
// Only rendered inside ClerkProvider (when CLERK_KEY is set), so useAuth() is safe.
// One automatic retry on a transient auth failure before showing the
// terminal "auth setup required" screen.
//
// Most named 401s are terminal (expired/invalid token, missing header) — but
// the backend's _extract_and_verify has a catch-all `except Exception -> 401`
// (utils/clerk_auth.py), so a momentary verification glitch under load also
// surfaces as a 401, not a 5xx. That transient 401 is what intermittently
// bricked the session in E2E. (A genuine JWKS-fetch failure is already a
// retryable 503, handled by the 5xx branch — not a 401; so is a database
// failure while provisioning, which is 503 DATABASE_UNAVAILABLE and, once
// retries are spent, shows a "service unavailable" screen instead of the
// auth-setup one.)
//
// We retry once on any 401: the cost of retrying a genuinely-terminal 401 is
// a single bounded ~800ms round-trip; the cost of NOT retrying a transient
// one is a permanently bricked session. `missing_email_claim` — the one
// structured-terminal 401 — is special-cased out before the retry check.
// The decision table lives in utils/authErrors.js (classifyProvisionFailure).
const MAX_AUTH_RETRIES = 1;
const AUTH_RETRY_DELAY_MS = 800;

function ClerkSync({ onLogin, onLogout, setLoading, onProvisionError, onSignOutReady }) {
  const { isSignedIn, isLoaded, getToken, signOut } = useAuth();
  const prevSignedIn = useRef(null);
  // Prevent retrying /auth/user after a failure — reset only on sign-out
  const provisionAttempted = useRef(false);
  // Counts consecutive transient failures so we retry a bounded number of
  // times. Reset on success and on sign-out.
  const authRetryCount = useRef(0);
  // Tracks a scheduled retry timer so it can be cancelled on unmount / sign-out.
  const retryTimer = useRef(null);

  // Wire Clerk's token getter into HttpService
  useEffect(() => {
    setClerkTokenGetter(isLoaded && isSignedIn ? getToken : null);
  }, [isLoaded, isSignedIn, getToken]);

  // Expose Clerk's signOut to the parent App component
  useEffect(() => {
    if (onSignOutReady) onSignOutReady(signOut);
  }, [signOut, onSignOutReady]);

  // Sync Clerk auth state → local app state
  useEffect(() => {
    if (!isLoaded) return;

    const wasSignedIn = prevSignedIn.current;
    prevSignedIn.current = isSignedIn;

    // Provision the user, retrying a bounded number of times on a transient
    // failure (5xx, network error, or a first 401) before surfacing a
    // terminal provision error.
    const provision = () => {
      AuthService.getCurrentUser()
        .then(userData => {
          authRetryCount.current = 0;
          if (userData) onLogin(userData);
          setLoading(false);
        })
        .catch((err) => {
          const outcome = classifyProvisionFailure(err, authRetryCount.current < MAX_AUTH_RETRIES);
          if (outcome.retry) {
            // Retry once. Keep loading=true so the user sees the spinner, not
            // a flash of the error screen, while the retry is in flight.
            authRetryCount.current += 1;
            retryTimer.current = setTimeout(provision, AUTH_RETRY_DELAY_MS);
            return;
          }

          setLoading(false);
          if (outcome.transient) {
            // Exhausted retries on a transient error — allow a fresh attempt
            // on the next auth event rather than locking the session.
            provisionAttempted.current = false;
            authRetryCount.current = 0;
          }
          if (outcome.error) onProvisionError(outcome.error);
        });
    };

    if (isSignedIn) {
      if (provisionAttempted.current) return; // don't retry after failure
      provisionAttempted.current = true;
      provision();
    } else {
      provisionAttempted.current = false; // reset on sign-out so next sign-in works
      authRetryCount.current = 0;
      if (retryTimer.current) {
        clearTimeout(retryTimer.current);
        retryTimer.current = null;
      }
      if (wasSignedIn === true) {
        // Signed out after being signed in
        onLogout();
      }
      setLoading(false);
    }

    return () => {
      if (retryTimer.current) {
        clearTimeout(retryTimer.current);
        retryTimer.current = null;
      }
    };
  }, [isLoaded, isSignedIn, onLogin, onLogout, setLoading, onProvisionError]);

  return null;
}

function App() {
  const basePath = document.getElementById('root')?.getAttribute('data-base-path') || '';
  const signOutRef = useRef(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [_sessionExpired, setSessionExpired] = useState(false);
  const [provisionError, setProvisionError] = useState(null);

  // Theme state for V2. The setter MUST be destructured — without it the
  // Profile page's Dark Mode toggle has no way to change the theme.
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved === 'true';
  });

  useEffect(() => {
    localStorage.setItem('darkMode', darkMode);
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  // Update Sentry user context when user state changes
  useEffect(() => {
    if (user) {
      Sentry.setUser({ id: user.id, username: user.username });
      analytics.identify(user.id, { username: user.username });
    } else {
      Sentry.setUser(null);
      analytics.reset();
    }
  }, [user]);

  // Pending-account-deletion state for the recovery banner. Fetched once
  // when the user authenticates; refreshed when they cancel. Kept null on
  // failure so a transient error doesn't show a stale or wrong banner.
  const [deletionStatus, setDeletionStatus] = useState(null);
  useEffect(() => {
    if (!isAuthenticated) {
      setDeletionStatus(null);
      return;
    }
    AccountService.getDeletionStatus()
      .then(setDeletionStatus)
      .catch((err) => {
        console.error('Failed to fetch account deletion status:', err);
        setDeletionStatus(null);
      });
  }, [isAuthenticated]);

  const clearDeletionStatus = useCallback(() => {
    setDeletionStatus({ pending: false });
  }, []);

  useEffect(() => {
    // Listen for auth failures from HttpService
    const unsubscribe = onAuthFailure(() => {
      setSessionExpired(true);
      setIsAuthenticated(false);
      setUser(null);
    });

    // Global 429 feedback: the rate-limiter's "try again in N minute(s)"
    // message used to be dropped, leaving each caller's generic failure copy.
    const unsubscribeRateLimit = onRateLimited((message) => {
      toast.error(message);
    });

    const initApp = async () => {
      // Fetch CSRF Token
      try {
        const response = await axios.get('/api/v1/csrf-token');
        if (response.data.csrf_token) {
          axios.defaults.headers.common['X-CSRFToken'] = response.data.csrf_token;
        }
      } catch (e) {
        console.error("Failed to fetch CSRF token", e);
      }

      if (CLERK_KEY) {
        // ClerkSync handles auth initialisation and will call setLoading(false)
        return;
      }

      // No Clerk configured — should not happen in production but keep as a fallback
      setLoading(false);
    };

    initApp();
    return () => {
      unsubscribe();
      unsubscribeRateLimit();
    };
  }, []);

  const handleLogin = useCallback((userData) => {
    setUser(userData);
    setIsAuthenticated(true);
    setSessionExpired(false);
  }, []);

  // Clears local auth state — used as ClerkSync's onLogout reaction
  const handleLogout = useCallback(() => {
    setClerkTokenGetter(null);
    setUser(null);
    setIsAuthenticated(false);
    setSessionExpired(false);
  }, []);

  // Triggers Clerk sign-out then clears local state — used by UI buttons
  const handleUILogout = useCallback(() => {
    handleLogout();
    if (signOutRef.current) {
      signOutRef.current().catch(err => console.error('Clerk sign-out failed:', err));
    }
  }, [handleLogout]);

  // Common Layout Props — Layout only consumes onLogout. darkMode is wired
  // directly to <ThemeStyles> at the App root, so it doesn't need to flow
  // through Layout. `user` is included for routes that read it via props
  // spread on inner pages (DashboardV2 destructures user as a separate prop).
  const layoutProps = {
    onLogout: handleUILogout,
    user: user
  };

  return (
    <ThemeProvider>
      {/*
        ThemeStyles MUST render at App root so CSS variables (--bg-main,
        --primary, --error, etc.) resolve before the loading spinner and
        provision-error UI paint. Previously only mounted inside LayoutV2,
        which left initial-load surfaces unstyled.
      */}
      <ThemeStyles darkMode={darkMode} />
      {/*
        ClerkSync MUST mount before the loading spinner so it can resolve
        auth state and call setLoading(false). It renders null itself.
      */}
      {CLERK_KEY && (
        <ClerkSync
          onLogin={handleLogin}
          onLogout={handleLogout}
          setLoading={setLoading}
          onProvisionError={setProvisionError}
          onSignOutReady={(fn) => { signOutRef.current = fn; }}
        />
      )}

      {provisionError && !isAuthenticated && (
        <div className="flex justify-center items-center min-h-screen bg-[var(--bg-main)] p-4">
          <Card className="max-w-md w-full p-8 text-center">
            <h2 className="text-xl font-bold text-[var(--error)] mb-4">{provisionErrorCopy(provisionError).title}</h2>
            <p className="text-[var(--text-secondary)] mb-4">{provisionError.message}</p>
            <p className="text-sm text-[var(--text-muted)] mb-6">
              {provisionErrorCopy(provisionError).hint}
            </p>
            <Button variant="primary" onClick={() => window.location.reload()}>
              Refresh
            </Button>
          </Card>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center min-h-screen bg-[var(--bg-main)]" role="status" aria-label="Loading">
          <Loader2 className="animate-spin text-[var(--primary)]" size={48} />
          <span className="sr-only">Loading...</span>
        </div>
      ) : provisionError && !isAuthenticated ? null : (
      <Router basename={basePath}>
        <ErrorBoundary>
          {isAuthenticated && deletionStatus?.pending && (
            <PendingDeletionBanner
              scheduledDeletionAt={deletionStatus.scheduled_deletion_at}
              subscriptionWillBeCanceled={Boolean(deletionStatus.subscription_will_be_canceled)}
              onCancelled={clearDeletionStatus}
            />
          )}
          <PaywallProvider>
          <Routes>
            {/* Clerk Auth Routes */}
            <Route
              path="/clerk-login/*"
              element={
                isAuthenticated
                  ? <Navigate to="/dashboard" replace />
                  : provisionError
                    ? <Navigate to="/" replace />
                    : <ClerkLogin />
              }
            />
            <Route
              path="/clerk-register/*"
              element={
                isAuthenticated
                  ? <Navigate to="/dashboard" replace />
                  : <ClerkRegister />
              }
            />

            {/* Legacy /login and /register redirect to Clerk UI */}
            <Route
              path="/login"
              element={
                isAuthenticated
                  ? <Navigate to="/dashboard" replace />
                  : <Navigate to="/clerk-login" replace />
              }
            />
            <Route
              path="/register"
              element={
                isAuthenticated
                  ? <Navigate to="/dashboard" replace />
                  : <Navigate to="/clerk-register" replace />
              }
            />

            {/* Main Application Routes (V2) */}
            <Route
              path="/dashboard"
              element={
                isAuthenticated
                  ? <LayoutV2 {...layoutProps}><DashboardV2 user={user} /></LayoutV2>
                  : <Navigate to="/login" replace />
              }
            />

            {/* Subscription / Billing */}
            <Route
              path="/subscription"
              element={
                isAuthenticated
                  ? <LayoutV2 {...layoutProps}><SubscriptionV2 /></LayoutV2>
                  : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/subscription/success"
              element={
                isAuthenticated
                  ? <LayoutV2 {...layoutProps}><SubscriptionSuccess /></LayoutV2>
                  : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/subscription/cancel"
              element={
                isAuthenticated
                  ? <LayoutV2 {...layoutProps}><SubscriptionCancel /></LayoutV2>
                  : <Navigate to="/login" replace />
              }
            />

            {/* Inventory Routes */}
            <Route
              path="/inventory"
              element={
                isAuthenticated
                  ? <LayoutV2 {...layoutProps}><InventoryV2 /></LayoutV2>
                  : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/inventory/new"
              element={
                isAuthenticated
                  ? <LayoutV2 {...layoutProps} fullWidthContent><AddAsset /></LayoutV2>
                  : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/inventory/edit/:id"
              element={
                isAuthenticated
                  ? <LayoutV2 {...layoutProps} fullWidthContent><AddAsset /></LayoutV2>
                  : <Navigate to="/login" replace />
              }
            />
            <Route path="/inventory/manual-entry" element={<Navigate to="/inventory" replace />} />
            {/* Dynamic :id route grouped last for readability. React Router v6 ranks
                static segments (new, edit, manual-entry) above dynamic ones regardless
                of declaration order, so this ordering isn't strictly required. */}
            <Route
              path="/inventory/:id"
              element={
                isAuthenticated
                  ? <LayoutV2 {...layoutProps} fullWidthContent><ItemDetails /></LayoutV2>
                  : <Navigate to="/login" replace />
              }
            />

            {/* Spaces / Rooms Routes */}
            <Route
              path="/spaces"
              element={
                isAuthenticated
                  ? <LayoutV2 {...layoutProps}><SpacesV2 /></LayoutV2>
                  : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/spaces/new"
              element={
                isAuthenticated
                  ? <LayoutV2 {...layoutProps} fullWidthContent><AddRoom /></LayoutV2>
                  : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/room/:id"
              element={
                isAuthenticated
                  ? <LayoutV2 {...layoutProps} fullWidthContent><RoomDetailsV2 /></LayoutV2>
                  : <Navigate to="/login" replace />
              }
            />
            {/* Floor detail create/edit lives on a dedicated page (replacing the
                old FloorDetailModal), matching the paint modal→page migration. */}
            <Route
              path="/room/:roomId/floor-details/new"
              element={
                isAuthenticated
                  ? <LayoutV2 {...layoutProps}><FloorDetailForm /></LayoutV2>
                  : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/room/:roomId/floor-details/:floorId/edit"
              element={
                isAuthenticated
                  ? <LayoutV2 {...layoutProps}><FloorDetailForm /></LayoutV2>
                  : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/spaces/edit/:id"
              element={
                isAuthenticated
                  ? <LayoutV2 {...layoutProps}><RoomEdit /></LayoutV2>
                  : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/house-floor-plan/:id"
              element={
                isAuthenticated
                  ? <LayoutV2 {...layoutProps} fullWidthContent><HouseFloorPlanEditor /></LayoutV2>
                  : <Navigate to="/login" replace />
              }
            />

            {/* Paint Library — user's shared paint collection */}
            <Route
              path="/paints"
              element={
                isAuthenticated
                  ? <LayoutV2 {...layoutProps}><PaintLibrary /></LayoutV2>
                  : <Navigate to="/login" replace />
              }
            />
            {/* Paint create/edit lives on a dedicated page so the form is
                roomy and the URL is shareable (PR replacing the old modal). */}
            <Route
              path="/paints/new"
              element={
                isAuthenticated
                  ? <LayoutV2 {...layoutProps}><PaintForm /></LayoutV2>
                  : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/paints/:id/edit"
              element={
                isAuthenticated
                  ? <LayoutV2 {...layoutProps}><PaintForm /></LayoutV2>
                  : <Navigate to="/login" replace />
              }
            />

            {/* House Management Routes */}
            <Route
              path="/houses"
              element={
                isAuthenticated
                  ? <LayoutV2 {...layoutProps}><HouseListV2 /></LayoutV2>
                  : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/houses/add"
              element={
                isAuthenticated
                  ? <LayoutV2 {...layoutProps}><HouseFormV2 /></LayoutV2>
                  : <Navigate to="/login" replace />
              }
            />
            {/*
              Legacy onboarding route. The dedicated lite form has been
              merged into HouseFormV2, which auto-detects an existing
              primary house and redirects to /houses/{id}/edit. Keep this
              redirect for old bookmarks / external links / shared
              screenshots that still point here.
            */}
            <Route
              path="/onboarding/house"
              element={
                isAuthenticated
                  ? <Navigate to="/houses/add" replace />
                  : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/houses/:id"
              element={
                isAuthenticated
                  ? <LayoutV2 {...layoutProps}><HouseDetailsV2 /></LayoutV2>
                  : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/houses/:id/edit"
              element={
                isAuthenticated
                  ? <LayoutV2 {...layoutProps}><HouseFormV2 /></LayoutV2>
                  : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/houses/:houseId/utilities/:utilityId"
              element={
                isAuthenticated
                  ? <LayoutV2 {...layoutProps}><UtilityDetailsPage /></LayoutV2>
                  : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/houses/:houseId/insurance/new"
              element={
                isAuthenticated
                  ? <LayoutV2 {...layoutProps}><InsuranceForm /></LayoutV2>
                  : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/houses/:houseId/insurance/:policyId/edit"
              element={
                isAuthenticated
                  ? <LayoutV2 {...layoutProps}><InsuranceForm /></LayoutV2>
                  : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/houses/:houseId/mortgages/new"
              element={
                isAuthenticated
                  ? <LayoutV2 {...layoutProps}><MortgageForm /></LayoutV2>
                  : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/houses/:houseId/mortgages/:mortgageId/edit"
              element={
                isAuthenticated
                  ? <LayoutV2 {...layoutProps}><MortgageForm /></LayoutV2>
                  : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/houses/:houseId/mortgages/:mortgageId/statements/new"
              element={
                isAuthenticated
                  ? <LayoutV2 {...layoutProps}><MortgageStatementForm /></LayoutV2>
                  : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/houses/:houseId/mortgages/:mortgageId/statements/:statementId/edit"
              element={
                isAuthenticated
                  ? <LayoutV2 {...layoutProps}><MortgageStatementForm /></LayoutV2>
                  : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/houses/:houseId/mortgages/:mortgageId/amortization"
              element={
                isAuthenticated
                  ? <LayoutV2 {...layoutProps}><MortgageAmortization /></LayoutV2>
                  : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/houses/:houseId/mortgages/:mortgageId"
              element={
                isAuthenticated
                  ? <LayoutV2 {...layoutProps}><MortgageDetails /></LayoutV2>
                  : <Navigate to="/login" replace />
              }
            />

            {/* Utility Routes */}
            <Route
              path="/profile"
              element={
                isAuthenticated
                  ? (
                    <LayoutV2 {...layoutProps}>
                      {/* darkMode + setDarkMode are passed directly (not via
                          layoutProps) because Profile is the only route that
                          owns the theme toggle. */}
                      <ProfileV2 {...layoutProps} darkMode={darkMode} setDarkMode={setDarkMode} />
                    </LayoutV2>
                  )
                  : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/maintenance"
              element={
                isAuthenticated
                  ? <LayoutV2 {...layoutProps}>
                      <FeatureGate enabled={FEATURES.maintenance} title="Maintenance">
                        <MaintenanceV2 />
                      </FeatureGate>
                    </LayoutV2>
                  : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/maintenance/new"
              element={
                isAuthenticated
                  ? <LayoutV2 {...layoutProps} fullWidthContent>
                      <FeatureGate enabled={FEATURES.maintenance} title="Maintenance">
                        <CreateTask />
                      </FeatureGate>
                    </LayoutV2>
                  : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/maintenance/edit/:id"
              element={
                isAuthenticated
                  ? <LayoutV2 {...layoutProps} fullWidthContent>
                      <FeatureGate enabled={FEATURES.maintenance} title="Maintenance">
                        <CreateTask />
                      </FeatureGate>
                    </LayoutV2>
                  : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/documents"
              element={
                isAuthenticated
                  ? <LayoutV2 {...layoutProps}><DocumentsV2 /></LayoutV2>
                  : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/models3d"
              element={
                isAuthenticated
                  ? <LayoutV2 {...layoutProps}>
                      <FeatureGate enabled={FEATURES.models3d} title="3D Models">
                        <Models3DV2 />
                      </FeatureGate>
                    </LayoutV2>
                  : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/notifications"
              element={
                isAuthenticated
                  ? <LayoutV2 {...layoutProps}><NotificationsV2 /></LayoutV2>
                  : <Navigate to="/login" replace />
              }
            />

            {/* Legacy V2 Route Redirects */}
            <Route path="/v2/dashboard" element={<Navigate to="/dashboard" replace />} />
            <Route path="/v2/inventory" element={<Navigate to="/inventory" replace />} />
            <Route path="/v2/spaces" element={<Navigate to="/spaces" replace />} />
            <Route path="/v2/houses" element={<Navigate to="/houses" replace />} />
            <Route path="/v2/profile" element={<Navigate to="/profile" replace />} />

            {/* Catch-all Redirect */}
            <Route
              path="/"
              element={<Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />}
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </PaywallProvider>
        </ErrorBoundary>
      </Router>
      )}
    </ThemeProvider>
  );
}

export default App;
